import { useState, useEffect } from 'react'
import AdminSidebar from '../../components/admin/AdminSidebar'
import './AdminProductsPage.css'
import { apiFetch } from '../../utils/api'
import { uploadImageToCloudinary } from '../../utils/cloudinary'

// const API = process.env.REACT_APP_API_URL || 'http://localhost:8000'

const CATEGORIES = ['SaaS', 'Fintech', 'EdTech', 'Web', 'Mobile', 'AI', 'Other']

const EMPTY_FORM = {
  name: '', tagline: '', description: '',
  how_it_works: '', features: [''],
  image_url: '', website_url: '',
  category: '', is_published: true,
}

// ── Product Form Modal ────────────────────────────────────────────
function ProductModal({ product, onClose, onSaved }) {
  const [form, setForm]       = useState(product ? {
    ...product,
    features: product.features?.length ? product.features : [''],
  } : { ...EMPTY_FORM })
  const [saving, setSaving]   = useState(false)
  const [error, setError]     = useState(null)
  const [imgError, setImgError] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState(null)
  const isEdit                = !!product

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }))

  const handleImageFile = async (file) => {
    if (!file) return
    setUploading(true)
    setUploadError(null)
    try {
      const url = await uploadImageToCloudinary(file)
      set('image_url', url)
      setImgError(false)
    } catch (err) {
      setUploadError(err.message || 'Upload failed.')
    } finally {
      setUploading(false)
    }
  }

  const setFeature = (i, val) => {
    const arr = [...form.features]
    arr[i] = val
    setForm(f => ({ ...f, features: arr }))
  }

  const addFeature    = () => setForm(f => ({ ...f, features: [...f.features, ''] }))
  const removeFeature = (i) => setForm(f => ({ ...f, features: f.features.filter((_, idx) => idx !== i) }))

  const save = async () => {
    if (!form.name || !form.tagline || !form.description) {
      setError('Name, tagline and description are required.')
      return
    }
    setSaving(true)
    setError(null)
    const payload = {
      ...form,
      features: form.features.filter(f => f.trim()),
    }
    try {
      const url    = isEdit ? `/api/admin/products/${product.id}` : `/api/admin/products`
      const method = isEdit ? 'PUT' : 'POST'
      const res    = await apiFetch(url, {
        method,
        body: JSON.stringify(payload),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.detail || 'Failed to save.')
      onSaved()
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className='apm-overlay' onClick={onClose}>
      <div className='apm-modal' onClick={e => e.stopPropagation()}>

        <div className='apm-modal-header'>
          <h2>{isEdit ? 'Edit Product' : 'Add Product'}</h2>
          <button className='apm-close' onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className='apm-modal-body'>
          {/* Row 1: Name + Category */}
          <div className='apm-row'>
            <div className='apm-field'>
              <label>Product Name <span>*</span></label>
              <input value={form.name} onChange={e => set('name', e.target.value)} placeholder='e.g. RastegFlow' />
            </div>
            <div className='apm-field'>
              <label>Category</label>
              <select value={form.category} onChange={e => set('category', e.target.value)}>
                <option value=''>Select category</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {/* Tagline */}
          <div className='apm-field'>
            <label>Tagline <span>*</span></label>
            <input value={form.tagline} onChange={e => set('tagline', e.target.value)} placeholder='One-line value proposition' />
          </div>

          {/* Description */}
          <div className='apm-field'>
            <label>Description <span>*</span></label>
            <textarea rows={3} value={form.description} onChange={e => set('description', e.target.value)} placeholder='What does this product do?' />
          </div>

          {/* How it works */}
          <div className='apm-field'>
            <label>How It Works <span className='opt'>(optional)</span></label>
            <textarea rows={3} value={form.how_it_works} onChange={e => set('how_it_works', e.target.value)} placeholder='Explain the product flow or mechanism…' />
          </div>

          {/* Features */}
          <div className='apm-field'>
            <label>Key Features <span className='opt'>(optional)</span></label>
            <div className='apm-features'>
              {form.features.map((f, i) => (
                <div key={i} className='apm-feature-row'>
                  <input
                    value={f}
                    onChange={e => setFeature(i, e.target.value)}
                    placeholder={`Feature ${i + 1}`}
                  />
                  {form.features.length > 1 && (
                    <button className='apm-remove-feat' onClick={() => removeFeature(i)} type='button'>×</button>
                  )}
                </div>
              ))}
              <button className='apm-add-feat' onClick={addFeature} type='button'>+ Add feature</button>
            </div>
          </div>

          {/* URLs */}
          <div className='apm-row'>
            <div className='apm-field'>
              <label>Image URL <span className='opt'>(optional)</span></label>
              <div className='apm-img-input-row'>
                <input
                  value={form.image_url}
                  onChange={e => { set('image_url', e.target.value); setImgError(false) }}
                  placeholder='https://res.cloudinary.com/… or upload →'
                />
                <label className='apm-upload-btn'>
                  {uploading ? 'Uploading…' : 'Upload'}
                  <input
                    type='file'
                    accept='image/*'
                    hidden
                    disabled={uploading}
                    onChange={e => { handleImageFile(e.target.files?.[0]); e.target.value = '' }}
                  />
                </label>
              </div>
              {uploadError && <p className='apm-upload-error'>{uploadError}</p>}
              {form.image_url && (
                <div className='apm-img-preview'>
                  {!imgError
                    ? <img src={form.image_url} alt='Preview' onError={() => setImgError(true)} />
                    : <span className='apm-img-preview-error'>Couldn't load an image from this URL</span>
                  }
                </div>
              )}
            </div>
            <div className='apm-field'>
              <label>Website URL <span className='opt'>(optional)</span></label>
              <input value={form.website_url} onChange={e => set('website_url', e.target.value)} placeholder='https://…' />
            </div>
          </div>

          {/* Published toggle */}
          <label className='apm-toggle'>
            <span>Published</span>
            <div className='apm-toggle-wrap'>
              <input type='checkbox' checked={form.is_published} onChange={e => set('is_published', e.target.checked)} />
              <span className='apm-toggle-track'><span className='apm-toggle-thumb'></span></span>
            </div>
            <span className='apm-toggle-hint'>{form.is_published ? 'Visible on site' : 'Hidden from site'}</span>
          </label>

          {error && <p className='apm-error'> {error}</p>}
        </div>

        <div className='apm-modal-footer'>
          <button className='apm-btn-cancel' onClick={onClose}>Cancel</button>
          <button className='apm-btn-save' onClick={save} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Product'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Delete confirm ────────────────────────────────────────────────
function DeleteConfirm({ product, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false)

  const confirm = async () => {
    setDeleting(true)
    try {
      await apiFetch(`/api/admin/products/${product.id}`, { method: 'DELETE' })
      onDeleted(product.id)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className='apm-overlay' onClick={onClose}>
      <div className='apm-confirm' onClick={e => e.stopPropagation()}>
        <h3>Delete "{product.name}"?</h3>
        <p>This cannot be undone. The product will be permanently removed from the database.</p>
        <div className='apm-confirm-actions'>
          <button className='apm-btn-cancel' onClick={onClose}>Cancel</button>
          <button className='apm-btn-delete' onClick={confirm} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Yes, Delete'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Product card in admin list ────────────────────────────────────
function ProductCard({ product, onEdit, onDelete, onTogglePublish }) {
  const [toggling, setToggling] = useState(false)

  const handleToggle = async () => {
    setToggling(true)
    try {
      const res  = await apiFetch(`/api/admin/products/${product.id}/publish`, { method: 'PATCH' })
      const data = await res.json()
      onTogglePublish(product.id, data.is_published)
    } finally {
      setToggling(false)
    }
  }

  return (
    <div className={`app-card ${!product.is_published ? 'app-card--draft' : ''}`}>
      <div className='app-card-img'>
        {product.image_url
          ? <img src={product.image_url} alt={product.name} />
          : <span className='app-card-img-placeholder'></span>
        }
        <span className={`app-card-status ${product.is_published ? 'app-card-status--live' : 'app-card-status--draft'}`}>
          {product.is_published ? 'Live' : 'Draft'}
        </span>
        {product.category && <span className='app-card-category'>{product.category}</span>}
      </div>

      <div className='app-card-body'>
        <h3 className='app-card-name'>{product.name}</h3>
        <p className='app-card-tagline'>{product.tagline}</p>
        <p className='app-card-desc'>{product.description}</p>

        {product.features?.length > 0 && (
          <div className='app-card-features'>
            {product.features.slice(0, 3).map((f, i) => (
              <span key={i} className='app-card-feat'>{f}</span>
            ))}
            {product.features.length > 3 && (
              <span className='app-card-feat app-card-feat--more'>+{product.features.length - 3} more</span>
            )}
          </div>
        )}
      </div>

      <div className='app-card-actions'>
        <button className='app-action-btn' onClick={() => onEdit(product)} title='Edit'>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
          </svg>
          Edit
        </button>

        <button
          className={`app-action-btn app-action-btn--toggle ${product.is_published ? 'app-action-btn--hide' : 'app-action-btn--show'}`}
          onClick={handleToggle}
          disabled={toggling}
          title={product.is_published ? 'Unpublish' : 'Publish'}
        >
          {product.is_published
            ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
            : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
          }
          {toggling ? '…' : product.is_published ? 'Unpublish' : 'Publish'}
        </button>

        <button className='app-action-btn app-action-btn--delete' onClick={() => onDelete(product)} title='Delete'>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
          </svg>
          Delete
        </button>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────
export default function AdminProductsPage() {
  const [products, setProducts] = useState([])
  const [loading, setLoading]   = useState(true)
  const [error, setError]       = useState(null)
  const [modalOpen, setModal]   = useState(false)
  const [editing, setEditing]   = useState(null)
  const [deleting, setDeleting] = useState(null)
  const [search, setSearch]     = useState('')
  const [filter, setFilter]     = useState('all') // all | live | draft

  const load = async () => {
    setLoading(true)
    try {
      const res  = await apiFetch('/api/admin/products')
      const data = await res.json()
      setProducts(data.products || [])
    } catch {
      setError('Could not load products.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const handleSaved = () => { setModal(false); setEditing(null); load() }

  const handleDeleted = (id) => {
    setProducts(p => p.filter(x => x.id !== id))
    setDeleting(null)
  }

  const handleTogglePublish = (id, is_published) => {
    setProducts(p => p.map(x => x.id === id ? { ...x, is_published } : x))
  }

  const filtered = products.filter(p => {
    const matchFilter = filter === 'all' || (filter === 'live' ? p.is_published : !p.is_published)
    const term = search.toLowerCase()
    const matchSearch = !term || [p.name, p.tagline, p.category].some(f => f?.toLowerCase().includes(term))
    return matchFilter && matchSearch
  })

  const counts = {
    all: products.length,
    live: products.filter(p => p.is_published).length,
    draft: products.filter(p => !p.is_published).length,
  }

  return (
    <div className='app-shell'>
      <AdminSidebar counts={{ total: products.length }} />

      <main className='app-main'>
        {/* Topbar */}
        <div className='app-topbar'>
          <div>
            <h1 className='app-title'>Products</h1>
            <p className='app-sub'>{filtered.length} of {products.length} products</p>
          </div>
          <div className='app-topbar-right'>
            <div className='app-search-wrap'>
              <svg className='app-search-icon' viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input className='app-search' placeholder='Search products…' value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <button className='app-btn-add' onClick={() => { setEditing(null); setModal(true) }}>
              + Add Product
            </button>
          </div>
        </div>

        {/* Filter tabs */}
        <div className='app-filter-bar'>
          {[['all', 'All'], ['live', 'Live'], ['draft', 'Draft']].map(([key, label]) => (
            <button
              key={key}
              className={`app-filter-btn ${filter === key ? 'app-filter-btn--active' : ''}`}
              onClick={() => setFilter(key)}
            >
              {label}
              <span className='app-filter-count'>{counts[key]}</span>
            </button>
          ))}
        </div>

        {/* States */}
        {loading && <div className='app-state'><div className='app-spinner'></div><p>Loading…</p></div>}
        {error   && <div className='app-state app-state--error'><p> {error}</p></div>}

        {!loading && !error && filtered.length === 0 && (
          <div className='app-state'>
            <p>{search || filter !== 'all' ? 'No products match your filter.' : 'No products yet — add your first one!'}</p>
            {!search && filter === 'all' && (
              <button className='app-btn-add' onClick={() => setModal(true)}>+ Add Product</button>
            )}
          </div>
        )}

        {/* Product grid */}
        {!loading && !error && filtered.length > 0 && (
          <div className='app-grid'>
            {filtered.map((p, i) => (
              <div key={p.id} style={{ animationDelay: `${i * 0.04}s` }} className='app-card-wrap'>
                <ProductCard
                  product={p}
                  onEdit={prod => { setEditing(prod); setModal(true) }}
                  onDelete={setDeleting}
                  onTogglePublish={handleTogglePublish}
                />
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Add/Edit modal */}
      {modalOpen && (
        <ProductModal
          product={editing}
          onClose={() => { setModal(false); setEditing(null) }}
          onSaved={handleSaved}
        />
      )}

      {/* Delete confirm */}
      {deleting && (
        <DeleteConfirm
          product={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  )
}